import * as React from 'react';
import {useFormikContext} from 'formik';
import styled from 'styled-components';
import CollapseContainer, {CollapseContainerProps} from 'containers/CollapseContainer';
import BoxContainer from 'containers/BoxContainer';

export type GroupContainerProps<T extends object> = {
  title?: string;
  property?: keyof T;
  collapse?: boolean;
  disabled?: boolean;
} & Pick<CollapseContainerProps,
  | 'label'
  | 'onChange'>;

type ContentProps = {
  compact?: boolean;
}

const Content = styled.div<ContentProps>`
  padding: ${({compact}) => compact ? '10px' : '0 10px 10px 10px'};
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  padding-top: 20px;
  padding-left: 20px;
  display: block;
`;

/**
 * This component wraps content in a group with collapse containers.
 * @param {React.PropsWithChildren<GroupContainerProps<T>>} props
 * @return {React.ReactElement<React.PropsWithChildren<GroupContainerProps<T>>>}
 * @constructor
 */
export default function GroupContainer
  <T extends object, V extends object = any>(
    props: React.PropsWithChildren<GroupContainerProps<T>>,
): React.ReactElement<React.PropsWithChildren<GroupContainerProps<T>>> {
  const {values, setFieldValue, submitForm} = useFormikContext<V>();

  const Container = (
    props.collapse
  ) ? CollapseContainer : 'div';

  /**
   * Get the default checked of collapse.
   * @return {CollapseContainerProps<defaultChecked>}
   */
  function defaultChecked() {
    // @ts-ignore
    return values[props.property]?.active;
  }

  /**
   * This change collapse event handler.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setFieldValue(`${props.property}.active`, event.target.checked);
    submitForm();

    props.onChange && props.onChange(event);
  }

  /**
   * Render title.
   * @return {JSX.Element | undefined}
   */
  function renderTitle(): JSX.Element | undefined {
    return props.title ? (
      <Title>
        {props.title}
      </Title>
    ) : undefined;
  }

  return (
    <BoxContainer disabled={props.disabled}>
      {renderTitle()}
      <Container
        label={props.label}
        defaultChecked={defaultChecked()}
        onChange={handleChange}
      >
        <Content compact={!props.collapse}>
          {props.children}
        </Content>
      </Container>
    </BoxContainer>
  );
}
